export interface EmailTemplate {
  subject: string;
  body: string;
  htmlBody?: string;
}

export class EmailNotificationService {
  // Email templates for different scenarios
  private static readonly EMAIL_TEMPLATES = {
    kycFailed: {
      subject: 'KYC Verification Failed - Action Required',
      body: `Dear {userName},

We regret to inform you that your KYC (Know Your Customer) verification has failed. 

Reason: {reason}

Please review your submitted documents and ensure they meet our requirements:
- All documents must be valid and not expired
- Document numbers must be clearly visible
- Photos must be high quality and legible
- All required fields must be completed

You can resubmit your KYC documents by logging into your account and navigating to the KYC section.

If you have any questions or need assistance, please contact our support team.

Best regards,
Live Trading Studio Team`,
      htmlBody: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #d32f2f;">KYC Verification Failed</h2>
          <p>Dear {userName},</p>
          <p>We regret to inform you that your KYC (Know Your Customer) verification has failed.</p>
          <div style="background-color: #ffebee; padding: 15px; border-left: 4px solid #d32f2f; margin: 20px 0;">
            <strong>Reason:</strong> {reason}
          </div>
          <p>Please review your submitted documents and ensure they meet our requirements:</p>
          <ul>
            <li>All documents must be valid and not expired</li>
            <li>Document numbers must be clearly visible</li>
            <li>Photos must be high quality and legible</li>
            <li>All required fields must be completed</li>
          </ul>
          <p>You can resubmit your KYC documents by logging into your account and navigating to the KYC section.</p>
          <p>If you have any questions or need assistance, please contact our support team.</p>
          <br>
          <p>Best regards,<br>Live Trading Studio Team</p>
        </div>
      `
    },
    kycReminder: {
      subject: 'Complete Your KYC Verification - Account Access Pending',
      body: `Dear {userName},

Your account is currently pending KYC (Know Your Customer) verification. To access all trading features and ensure compliance with regulatory requirements, please complete your KYC verification.

Required Documents:
1. National ID or Passport
2. Driver's License or Government ID Card
3. Tax Identification Number

Please log into your account and navigate to the KYC section to submit your documents.

If you have any questions or need assistance, please contact our support team.

Best regards,
Live Trading Studio Team`,
      htmlBody: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #f57c00;">Complete Your KYC Verification</h2>
          <p>Dear {userName},</p>
          <p>Your account is currently pending KYC (Know Your Customer) verification. To access all trading features and ensure compliance with regulatory requirements, please complete your KYC verification.</p>
          <div style="background-color: #fff3e0; padding: 15px; border-left: 4px solid #f57c00; margin: 20px 0;">
            <strong>Required Documents:</strong>
            <ol>
              <li>National ID or Passport</li>
              <li>Driver's License or Government ID Card</li>
              <li>Tax Identification Number</li>
            </ol>
          </div>
          <p>Please log into your account and navigate to the KYC section to submit your documents.</p>
          <p>If you have any questions or need assistance, please contact our support team.</p>
          <br>
          <p>Best regards,<br>Live Trading Studio Team</p>
        </div>
      `
    },
    welcomeEmail: {
      subject: 'Welcome to Live Trading Studio - Complete Your Setup',
      body: `Dear {userName},

Welcome to Live Trading Studio! We're excited to have you on board.

Your account has been created successfully. To get started with trading, please complete your KYC (Know Your Customer) verification.

Next Steps:
1. Complete your KYC verification by submitting required documents
2. Wait for approval (usually within 24-48 hours)
3. Start trading with our advanced platform

If you have any questions or need assistance, please contact our support team.

Best regards,
Live Trading Studio Team`,
      htmlBody: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #2AB7A6;">Welcome to Live Trading Studio!</h2>
          <p>Dear {userName},</p>
          <p>Welcome to Live Trading Studio! We're excited to have you on board.</p>
          <p>Your account has been created successfully. To get started with trading, please complete your KYC (Know Your Customer) verification.</p>
          <div style="background-color: #e8f5e8; padding: 15px; border-left: 4px solid #2AB7A6; margin: 20px 0;">
            <strong>Next Steps:</strong>
            <ol>
              <li>Complete your KYC verification by submitting required documents</li>
              <li>Wait for approval (usually within 24-48 hours)</li>
              <li>Start trading with our advanced platform</li>
            </ol>
          </div>
          <p>If you have any questions or need assistance, please contact our support team.</p>
          <br>
          <p>Best regards,<br>Live Trading Studio Team</p>
        </div>
      `
    },
    approvalEmail: {
      subject: 'Account Approved - Welcome to Live Trading Studio!',
      body: `Dear {userName},

Great news! Your account has been approved and you now have full access to all trading features.

Your KYC verification has been completed successfully, and you can start trading immediately.

Features now available:
- Real-time market data
- Advanced trading tools
- Portfolio management
- Risk management tools
- 24/7 customer support

If you have any questions or need assistance, please contact our support team.

Happy trading!

Best regards,
Live Trading Studio Team`,
      htmlBody: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #4caf50;">Account Approved!</h2>
          <p>Dear {userName},</p>
          <p>Great news! Your account has been approved and you now have full access to all trading features.</p>
          <p>Your KYC verification has been completed successfully, and you can start trading immediately.</p>
          <div style="background-color: #e8f5e8; padding: 15px; border-left: 4px solid #4caf50; margin: 20px 0;">
            <strong>Features now available:</strong>
            <ul>
              <li>Real-time market data</li>
              <li>Advanced trading tools</li>
              <li>Portfolio management</li>
              <li>Risk management tools</li>
              <li>24/7 customer support</li>
            </ul>
          </div>
          <p>If you have any questions or need assistance, please contact our support team.</p>
          <p>Happy trading!</p>
          <br>
          <p>Best regards,<br>Live Trading Studio Team</p>
        </div>
      `
    },
    rejectionEmail: {
      subject: 'Account Application Rejected - Action Required',
      body: `Dear {userName},

We regret to inform you that your account application has been rejected.

Reason: {reason}

You may be able to reapply after addressing the issues mentioned above. Please ensure all information is accurate and complete before resubmitting.

If you believe this decision was made in error or have any questions, please contact our support team.

Best regards,
Live Trading Studio Team`,
      htmlBody: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #d32f2f;">Account Application Rejected</h2>
          <p>Dear {userName},</p>
          <p>We regret to inform you that your account application has been rejected.</p>
          <div style="background-color: #ffebee; padding: 15px; border-left: 4px solid #d32f2f; margin: 20px 0;">
            <strong>Reason:</strong> {reason}
          </div>
          <p>You may be able to reapply after addressing the issues mentioned above. Please ensure all information is accurate and complete before resubmitting.</p>
          <p>If you believe this decision was made in error or have any questions, please contact our support team.</p>
          <br>
          <p>Best regards,<br>Live Trading Studio Team</p>
        </div>
      `
    }
  };

  // Send email notification (this would integrate with your email service)
  static async sendEmail(
    to: string,
    template: keyof typeof EmailNotificationService.EMAIL_TEMPLATES,
    variables: Record<string, string>
  ): Promise<boolean> {
    try {
      const emailTemplate = this.EMAIL_TEMPLATES[template];
      if (!emailTemplate) {
        throw new Error(`Email template '${template}' not found`);
      }

      // Replace variables in template
      let subject = emailTemplate.subject;
      let body = emailTemplate.body;
      let htmlBody = emailTemplate.htmlBody;

      Object.entries(variables).forEach(([key, value]) => {
        const regex = new RegExp(`{${key}}`, 'g');
        subject = subject.replace(regex, value);
        body = body.replace(regex, value);
        if (htmlBody) {
          htmlBody = htmlBody.replace(regex, value);
        }
      });

      // In a real implementation, this would send the email via your email service
      // For now, we'll log it and return success
      console.log('Email would be sent:', {
        to,
        subject,
        body,
        htmlBody
      });

      // TODO: Integrate with your email service (SendGrid, AWS SES, etc.)
      // Example with a hypothetical email service:
      // await emailService.send({
      //   to,
      //   subject,
      //   body,
      //   htmlBody
      // });

      return true;
    } catch (error) {
      console.error('Failed to send email:', error);
      return false;
    }
  }

  // Convenience methods for different email types
  static async sendKYCFailedEmail(userEmail: string, userName: string, reason: string): Promise<boolean> {
    return this.sendEmail(userEmail, 'kycFailed', {
      userName,
      reason
    });
  }

  static async sendKYCReminderEmail(userEmail: string, userName: string): Promise<boolean> {
    return this.sendEmail(userEmail, 'kycReminder', {
      userName
    });
  }

  static async sendWelcomeEmail(userEmail: string, userName: string): Promise<boolean> {
    return this.sendEmail(userEmail, 'welcomeEmail', {
      userName
    });
  }

  static async sendApprovalEmail(userEmail: string, userName: string): Promise<boolean> {
    return this.sendEmail(userEmail, 'approvalEmail', {
      userName
    });
  }

  static async sendRejectionEmail(userEmail: string, userName: string, reason: string): Promise<boolean> {
    return this.sendEmail(userEmail, 'rejectionEmail', {
      userName,
      reason
    });
  }
}
